// Phase 9B — AgentWorkloadPage.
//
// Analyst / Agent workload dashboard. Shows agent roster, task
// pipeline by status, workload stats, and capacity utilization.

import { useState, useMemo } from "react";
import type { CSSProperties } from "react";
import {
  useAgents,
  useAgentTasks,
} from "../../agents/useAgentWorkload.ts";
import type {
  Agent,
  AgentTask,
  TaskStatus,
} from "../../agents/useAgentWorkload.ts";
import * as s from "./agent.styles.ts";

// ─── Constants ────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  lead_analyst: "Lead Analyst",
  market_analyst: "Market Analyst",
  risk_analyst: "Risk Analyst",
  data_analyst: "Data Analyst",
  automation_agent: "Automation",
  review_agent: "Review Agent",
};

const STATUS_TABS: { key: "all" | TaskStatus; label: string }[] = [
  { key: "all", label: "All" },
  { key: "unassigned", label: "Unassigned" },
  { key: "assigned", label: "Assigned" },
  { key: "in_progress", label: "In Progress" },
  { key: "blocked", label: "Blocked" },
  { key: "completed", label: "Completed" },
];

// ─── Agent Card ───────────────────────────────────────────────

function AgentCard({
  agent,
  taskCount,
}: {
  agent: Agent;
  taskCount: number;
}): JSX.Element {
  const util = agent.capacity > 0 ? (taskCount / agent.capacity) * 100 : 0;
  const utilClamped = Math.min(util, 100);

  const fillColor: CSSProperties =
    utilClamped >= 90
      ? { ...s.capacityFill, background: "var(--sz-error-text)" }
      : utilClamped >= 70
        ? { ...s.capacityFill, background: "var(--sz-amber)" }
        : s.capacityFill;

  return (
    <div style={agent.active ? s.agentCard : s.agentCardInactive}>
      <div style={{ display: "flex", gap: "var(--sz-space-3)", alignItems: "center" }}>
        <span style={s.agentAvatar}>{agent.avatar_icon}</span>
        <div style={{ flex: 1 }}>
          <div style={s.agentName}>{agent.name}</div>
          <div style={s.agentRole}>
            {ROLE_LABELS[agent.role] ?? agent.role.replace(/_/g, " ")}
          </div>
        </div>
      </div>
      <div style={s.agentMeta}>
        <div style={s.capacityBar}>
          <div
            style={{
              ...fillColor,
              width: `${utilClamped.toFixed(0)}%`,
            }}
          />
        </div>
        <span style={s.capacityLabel}>
          {String(taskCount)}/{String(agent.capacity)}
        </span>
      </div>
    </div>
  );
}

// ─── Task Row ─────────────────────────────────────────────────

function TaskRow({
  task,
  agentMap,
}: {
  task: AgentTask;
  agentMap: Map<string, Agent>;
}): JSX.Element {
  const dotColor = s.PRIORITY_COLORS[task.priority] ?? "var(--sz-text-muted)";
  const statusStyle = s.STATUS_STYLES[task.status] ?? s.STATUS_STYLES.unassigned;
  const agentName =
    task.agent_id && agentMap.has(task.agent_id)
      ? agentMap.get(task.agent_id)!.name
      : "Unassigned";

  return (
    <div style={s.taskRow}>
      <span style={{ ...s.taskPriorityDot, background: dotColor }} />
      <span style={s.taskTitle}>{task.title}</span>
      <span style={s.taskAgent}>{agentName}</span>
      <span style={s.taskEffort}>{String(task.effort_points)}pt</span>
      <span style={statusStyle}>{task.status.replace(/_/g, " ")}</span>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function AgentWorkloadPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<"all" | TaskStatus>("all");

  const agentsState = useAgents();
  const tasksState = useAgentTasks(
    activeTab === "all" ? undefined : activeTab,
  );

  const agentMap = useMemo(() => {
    const m = new Map<string, Agent>();
    for (const a of agentsState.agents) {
      m.set(a.id, a);
    }
    return m;
  }, [agentsState.agents]);

  // Count tasks per agent
  const agentTaskCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of tasksState.tasks) {
      if (t.agent_id && t.status !== "completed" && t.status !== "cancelled") {
        counts.set(t.agent_id, (counts.get(t.agent_id) ?? 0) + 1);
      }
    }
    return counts;
  }, [tasksState.tasks]);

  // Stats
  const totalTasks = tasksState.tasks.length;
  const activeTasks = tasksState.tasks.filter(
    (t) => t.status === "in_progress" || t.status === "assigned",
  ).length;
  const blockedTasks = tasksState.tasks.filter(
    (t) => t.status === "blocked",
  ).length;
  const completedTasks = tasksState.tasks.filter(
    (t) => t.status === "completed",
  ).length;
  const totalEffort = tasksState.tasks
    .filter((t) => t.status !== "completed" && t.status !== "cancelled")
    .reduce((sum, t) => sum + t.effort_points, 0);

  // Status counts for tab badges
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of tasksState.tasks) {
      counts[t.status] = (counts[t.status] ?? 0) + 1;
    }
    return counts;
  }, [tasksState.tasks]);

  return (
    <div style={s.agentRoot}>
      {/* Header */}
      <div style={s.agentHeader}>
        <div>
          <h2 style={s.agentTitle}>Agent Workload</h2>
          <div style={s.agentSubtitle}>
            Analyst roster · task pipeline · capacity planning
          </div>
        </div>
        <span style={s.sectionCount}>
          {String(agentsState.agents.length)} agents ·{" "}
          {String(totalTasks)} tasks
        </span>
      </div>

      {/* Stats row */}
      <div style={s.statsRow}>
        <div style={s.statCard}>
          <span style={s.statValue}>{String(totalTasks)}</span>
          <span style={s.statLabel}>Total Tasks</span>
        </div>
        <div style={s.statCard}>
          <span style={{ ...s.statValue, color: "var(--sz-amber)" }}>
            {String(activeTasks)}
          </span>
          <span style={s.statLabel}>Active</span>
        </div>
        <div style={s.statCard}>
          <span style={{ ...s.statValue, color: "var(--sz-error-text)" }}>
            {String(blockedTasks)}
          </span>
          <span style={s.statLabel}>Blocked</span>
        </div>
        <div style={s.statCard}>
          <span style={{ ...s.statValue, color: "var(--sz-success-text)" }}>
            {String(completedTasks)}
          </span>
          <span style={s.statLabel}>Completed</span>
        </div>
        <div style={s.statCard}>
          <span style={s.statValue}>{String(totalEffort)}</span>
          <span style={s.statLabel}>Open Effort</span>
        </div>
      </div>

      {/* Agent roster */}
      <div>
        <div style={s.sectionHeader}>
          <span style={s.sectionTitle}>Agent Roster</span>
          <span style={s.sectionCount}>
            {String(agentsState.agents.filter((a) => a.active).length)} active
          </span>
        </div>
        {agentsState.agents.length === 0 ? (
          <div style={{ ...s.emptyState, marginTop: "var(--sz-space-2)" }}>
            <span style={s.emptyIcon}>◈</span>
            <span style={s.emptyTitle}>No Agents</span>
            <span style={s.emptyNote}>
              Analyst agents will be provisioned as the system scales.
            </span>
          </div>
        ) : (
          <div style={{ ...s.agentsRow, marginTop: "var(--sz-space-2)" }}>
            {agentsState.agents.map((agent) => (
              <AgentCard
                key={agent.id}
                agent={agent}
                taskCount={agentTaskCounts.get(agent.id) ?? 0}
              />
            ))}
          </div>
        )}
      </div>

      {/* Task pipeline */}
      <div>
        <div style={s.sectionHeader}>
          <span style={s.sectionTitle}>Task Pipeline</span>
        </div>

        {/* Status tabs */}
        <div style={{ ...s.tabBar, marginTop: "var(--sz-space-2)" }}>
          {STATUS_TABS.map((tab) => {
            const count =
              tab.key === "all"
                ? totalTasks
                : (statusCounts[tab.key] ?? 0);
            return (
              <button
                key={tab.key}
                type="button"
                style={activeTab === tab.key ? s.tabItemActive : s.tabItem}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {count > 0 ? ` (${String(count)})` : ""}
              </button>
            );
          })}
        </div>

        {/* Task list */}
        {tasksState.tasks.length === 0 ? (
          <div style={{ ...s.emptyState, marginTop: "var(--sz-space-2)" }}>
            <span style={s.emptyIcon}>▤</span>
            <span style={s.emptyTitle}>No Tasks</span>
            <span style={s.emptyNote}>
              Tasks will appear here as boards generate work items
              for analyst review and processing.
            </span>
          </div>
        ) : (
          <div style={{ ...s.taskGrid, marginTop: "var(--sz-space-2)" }}>
            {tasksState.tasks.map((task) => (
              <TaskRow key={task.id} task={task} agentMap={agentMap} />
            ))}
          </div>
        )}
      </div>

      <span style={s.phantomBadge}>Phantom · simulated</span>
    </div>
  );
}
